import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Fingerprint } from "lucide-react";
import { scrollToSection } from "../lib/scroll";

const LINES = ["THE UNITED", "STATES OF", "AFRICA"];
const HERO_IMG =
  "https://images.unsplash.com/photo-1649299313612-48cc3493f62e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxhZnJpY2FuJTIweW91dGglMjB1bmlvbiUyMHBhc3Nwb3J0JTIwbGVhZGVyc2hpcCUyMGFmcmljYSUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3ODczMTg0MzZ8MA&ixlib=rb-4.1.0&q=85";

const STATS = [
  { value: "55", label: "Member States" },
  { value: "1.5B", label: "Continental Citizens" },
  { value: "350M", label: "African Diaspora" },
  { value: "01", label: "Unifier Passport" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex items-center overflow-hidden noise-overlay" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img src={HERO_IMG} alt="Golden map of a borderless Africa" className="w-full h-full object-cover opacity-40" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0C0E]/60 via-[#0A0C0E]/80 to-[#0A0C0E]" />
      <div className="absolute inset-0 gold-glow" />

      <motion.div style={{ opacity: contentOpacity }} className="relative z-10 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto w-full pt-32 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="flex items-center gap-4"
        >
          <img
            src="/kush-seal.png?v=3"
            alt="Official seal — United States of Africa, Kingdom of Kush"
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-full shadow-[0_0_40px_rgba(255,215,0,0.3)]"
            data-testid="hero-kush-seal"
          />
          <p className="font-official text-[11px] sm:text-xs tracking-[0.35em] uppercase text-[#00A651]">
            Initiated by Our Ancestors
          </p>
        </motion.div>

        <h1 className="font-display font-extrabold tracking-tight leading-[0.95] mt-10 text-4xl sm:text-5xl lg:text-6xl" data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.25 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
                className={`block ${i === 2 ? "text-[#00A651]" : "text-[#F7F5F0]"}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-8 max-w-xl text-base sm:text-lg text-[#8A99AD] leading-relaxed"
        >
          One continent. One people. One passport. A founding kingdom, fifty-four nations, 1.5 billion citizens —
          and 350 million people of African descent across the diaspora — united under a single banner:{" "}
          <span className="text-[#F7F5F0]">Africa Without Border</span>.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollToSection("#passport")}
            data-testid="get-passport-button"
            className="group inline-flex items-center gap-3 rounded-full bg-[#00A651] text-[#0A0C0E] font-display font-bold text-sm px-7 py-4 hover:bg-[#2FC36A] hover:shadow-[0_0_32px_rgba(0,166,81,0.4)] transition-all duration-300"
          >
            <Fingerprint className="w-4 h-4" />
            Claim Your Passport
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            onClick={() => scrollToSection("#join")}
            data-testid="join-movement-button"
            className="rounded-full border border-[#00A651]/40 text-[#00A651] font-display font-bold text-sm px-7 py-4 hover:border-[#00A651] hover:bg-[#00A651]/10 transition-all duration-300"
          >
            Join The Movement
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.25 }}
          className="mt-16 grid grid-cols-2 sm:grid-cols-4 max-w-2xl divide-x divide-[#00A651]/15 border-y border-[#00A651]/15"
          data-testid="hero-stats"
        >
          {STATS.map((s) => (
            <div key={s.label} className="px-4 sm:px-6 py-5">
              <p className="font-display font-extrabold text-2xl sm:text-3xl text-[#00A651]">{s.value}</p>
              <p className="font-official text-[10px] sm:text-[11px] tracking-[0.2em] uppercase text-[#8A99AD] mt-2">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
